import {
  ModalDialog,
  ModalDialogActions,
  ModalDialogDescription,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogTitle,
} from '@/components/ModalDialog'
import { Button } from '@/components/Button'
import type { TaxJobDetailsViewModel } from '@/features/jobs/details/tax-type/TaxJobDetailsViewModel'
import { observer } from 'mobx-react-lite'
import { ActionCard } from '@/features/jobs/details/components/ActionCard'
import { ScrolledAction } from '@/features/jobs/details/components/PageHeader'

export interface ReadyToFileConfirmationDialogProps {
  isOpen: boolean
  isPending: boolean
  onClose: () => unknown
  onConfirmReadyToFile: () => unknown
  testId?: string
  confirmButtonTestId?: string
  goBackButtonTestId?: string
}

/**
 * Dialog for confirming that the user is ready to file.
 */
export function ReadyToFileConfirmationDialog({
  isOpen,
  isPending,
  onClose,
  onConfirmReadyToFile,
  testId,
  confirmButtonTestId,
  goBackButtonTestId,
}: ReadyToFileConfirmationDialogProps) {
  return (
    <ModalDialog
      isOpen={isOpen}
      onClose={onClose}
      isDismissable={!isPending}
      testId={testId}
    >
      <ModalDialogHeader>
        <ModalDialogTitle>
          Are you ready for your Pro to work on your return?
        </ModalDialogTitle>
        <ModalDialogDescription>
          Once ready, your Pro will be able to start working on your tax return.
        </ModalDialogDescription>
      </ModalDialogHeader>
      <ModalDialogFooter>
        <ModalDialogActions>
          <Button
            label="Yes, I'm ready"
            variant="default"
            onPress={onConfirmReadyToFile}
            isPending={isPending}
            testId={confirmButtonTestId}
          />
          <Button
            label="Go back"
            variant="outline"
            isDisabled={isPending}
            onPress={onClose}
            testId={goBackButtonTestId}
          />
        </ModalDialogActions>
      </ModalDialogFooter>
    </ModalDialog>
  )
}

interface ReadyToFileConfirmationDialogContainerProps {
  vm: TaxJobDetailsViewModel
}

/**
 * Ready to file dialog container.
 */
export const ReadyToFileConfirmationDialogContainer = observer(
  function ReadyToFileConfirmationDialogContainer({
    vm: { progressionVm },
  }: ReadyToFileConfirmationDialogContainerProps) {
    return (
      <ReadyToFileConfirmationDialog
        isOpen={progressionVm.extensionVm.confirmReadyToFileModalVm.showing}
        isPending={progressionVm.extensionVm.confirmReadyToFile.pending}
        onClose={progressionVm.extensionVm.confirmReadyToFileModalVm.close}
        onConfirmReadyToFile={progressionVm.extensionVm.confirmReadyToFile}
      />
    )
  },
)

interface ReadyToFileActionCardContainerProps {
  vm: TaxJobDetailsViewModel
}

/**
 * Container for the action card + dialog for confirming that the user is ready to file.
 */
export const ReadyToFileActionCardContainer = observer(
  function ReadyToFileActionCardContainer({
    vm,
  }: ReadyToFileActionCardContainerProps) {
    return (
      <>
        <ActionCard
          kicker={vm.progressionVm.currentStepContent?.title}
          title="Pending your response"
        >
          <Button
            onPress={vm.progressionVm.extensionVm.readyToFile}
            label="My information is ready"
          />
        </ActionCard>
      </>
    )
  },
)

interface ReadyToFileScrolledActionContainerProps {
  vm: TaxJobDetailsViewModel
}

/**
 * Scrolled action for the header.
 */
export const ReadyToFileScrolledActionContainer = observer(
  function ReadyToFileScrolledActionContainer({
    vm,
  }: ReadyToFileScrolledActionContainerProps) {
    return (
      <ScrolledAction
        label="My information is ready"
        onPress={vm.progressionVm.extensionVm.readyToFile}
      />
    )
  },
)
